import React from "react"
import PrivacyExplanation from "../PrivacyExplanation"
import { Expandable } from "frontend/components/layout"
import { Field } from "frontend/components/forms"
import { Heading } from "frontend/components/text"
import { logUploadFlowEvent, t, Translation } from "frontend/utils"
import { isIncentivizedUpload } from "frontend/utils/upload"

import styles from "../UploadForm.module.css"

const getCategories = () => {
  const categories = window.upload_config.category_hash.top_categories
  const list = categories.map((category) => ({
    label: category.name,
    value: category.id,
  }))
  return [{ label: t("upload.form.category.placeholder"), value: 0, disabled: true }, ...list]
}

export const getFields = (file, slideshowType) => {
  const topTags = window.upload_config.top_tags
  const licenseOptions = window.upload_config.license_options.map((option) => ({
    label: option[0],
    value: `${option[1]}`,
  }))

  return {
    title: {
      required: true,
      label: t("upload.form.title.label"),
      type: "input",
      defaultValue: file.filename,
      skipRendering: true,
    },
    description: {
      required: true,
      label: t("upload.form.description.label"),
      characterLimit: 3000,
      type: "textarea",
      skipRendering: true,
      testId: "description",
    },
    category: {
      required: true,
      label: t("upload.form.category.label"),
      type: "dropdown",
      options: getCategories(),
      skipRendering: true,
      defaultValue: 0,
      testId: "category",
    },
    tags: {
      required: false,
      type: "taginput",
      label: t("upload.form.tags.label"),
      tagSuggestions: topTags,
      skipRendering: true,
      footerText: t("upload.form.tags.footer"),
    },
    privacy: {
      required: false,
      type: "selector",
      label: t("upload.form.privacy.label"),
      options: [
        { key: "public", label: t("upload.form.privacy.public") },
        { key: "private", label: t("upload.form.privacy.private") },
        { key: "onlyme", label: t("upload.form.privacy.onlyme") },
      ],
      defaultValue: "public",
      skipRendering: true,
      explanation: <PrivacyExplanation />,
      hidden: isIncentivizedUpload(),
    },
    license: {
      required: false,
      label: t("upload.form.license.label"),
      type: "dropdown",
      defaultValue: licenseOptions[0].value,
      options: licenseOptions,
      skipRendering: true,
      footerText: <Translation string="upload.form.license.footer" />,
    },
    allow_download: {
      required: false,
      label: t("upload.form.allow_download.label"),
      defaultValue: true,
      type: "checkbox",
      skipRendering: true,
    },
    allow_embed: {
      required: false,
      label: t("upload.form.allow_embed.label"),
      defaultValue: true,
      type: "checkbox",
      skipRendering: true,
    },
    allow_clips: {
      required: false,
      label: t("upload.form.allow_clip.label"),
      defaultValue: true,
      type: "checkbox",
      skipRendering: true,
      disabled: slideshowType === "infographic" || slideshowType === "document",
      disabledExplanation: t("upload.form.clipping_disabled"),
    },
  }
}

const logChangeEvent = (originalOnChange) => (id, newValue) => {
  originalOnChange(id, newValue)
  logUploadFlowEvent(`form_${id}`)
}

export const renderFields = (fieldProps) => {
  const privacyValue = fieldProps.values.get("privacy")

  fieldProps.onChange = logChangeEvent(fieldProps.onChange)

  return (
    <div className={styles.formFields}>
      <div className={styles.formColumns}>
        <div className={styles.formColumn}>
          <Field {...fieldProps} id="title" className={styles.field} />
          <Field {...fieldProps} id="description" className={styles.field} />
        </div>
        <div className={styles.formColumn}>
          <Field {...fieldProps} id="category" className={styles.field} />
          <Field {...fieldProps} id="tags" className={styles.field} />
          <Field {...fieldProps} id="privacy" className={styles.field} />
        </div>
      </div>
      <Expandable
        className={styles.advancedSettingsContainer}
        labelWhenExpanded={t("upload.form.advanced.hide")}
        labelWhenHidden={t("upload.form.advanced.show")}
      >
        <div className={`${styles.formColumn} ${styles.advancedSettings}`}>
          <Field {...fieldProps} id="license" />
          {privacyValue !== "onlyme" && <Heading size={4}>{t("upload.form.allow_to")}:</Heading>}
          {privacyValue !== "onlyme" && <Field {...fieldProps} id="allow_download" />}
          {privacyValue === "public" && <Field {...fieldProps} id="allow_embed" />}
          {privacyValue === "public" && <Field {...fieldProps} id="allow_clips" />}
        </div>
      </Expandable>
    </div>
  )
}
